/**
 * csvExport — Client-side CSV download helpers.
 *
 * All exports run in the browser. We never round-trip to a server because
 * Supabase row-level auth is already enforced for the active session.
 */

function escapeCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    try { return escapeCell(JSON.stringify(v)); } catch { return ""; }
  }
  const s = String(v);
  if (s.includes(",") || s.includes("\"") || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function rowsToCsv(rows: Record<string, any>[], columns?: string[]): string {
  if (!rows.length && !columns) return "";
  const cols = columns && columns.length
    ? columns
    : Array.from(rows.reduce((set, r) => {
        Object.keys(r || {}).forEach(k => set.add(k));
        return set;
      }, new Set<string>()));
  const lines = [cols.map(escapeCell).join(",")];
  for (const r of rows) {
    lines.push(cols.map(c => escapeCell(r?.[c])).join(","));
  }
  return lines.join("\r\n");
}

export function downloadCsv(filename: string, csv: string): void {
  // Prepend BOM so Excel autodetects UTF-8.
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

export function todayStamp(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${dd}`;
}

/**
 * Convenience wrapper — flattens rows to a sane column set, names the file
 * mi6-<kind>-YYYYMMDD.csv, and triggers a download.
 */
export function exportRows(kind: string, rows: Record<string, any>[], columns?: string[]): void {
  const csv = rowsToCsv(rows, columns);
  downloadCsv(`mi6-${kind}-${todayStamp()}.csv`, csv);
}
